/**
 * GOV13-A3 (train commit 17) — SuperAdmin session guard helpers (pure, testable).
 *
 * The cached `superadmin_user` is PROVISIONAL only: it lets the shell paint an identity, but the session must be
 * validated against the backend before any protected request is issued or `authenticated` becomes true. On an
 * expired-session 401 we clear ONLY the SuperAdmin keys (never Business/Firebase — plane isolation), exactly
 * once, to stop 401 storms and redirect loops. The fixed 8h TTL is unchanged; there is no silent extension and
 * no token is ever logged.
 */
export const SA_SESSION_KEY = 'superadmin_session';
export const SA_USER_KEY = 'superadmin_user';
export const SESSION_EXPIRED_MESSAGE = 'Your SuperAdmin session has expired. Sign in again to continue.';

export function readCachedSuperAdminSession(storage) {
  const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!s) return null;
  try {
    const token = s.getItem(SA_SESSION_KEY);
    const raw = s.getItem(SA_USER_KEY);
    if (token && raw) return { token, user: JSON.parse(raw) };
  } catch (_) { /* corrupt cache → treat as no session */ }
  return null;
}

/** Clear ONLY SuperAdmin keys. Business/Firebase keys are never touched here (independent sessions). */
export function clearSuperAdminSessionKeys(storage) {
  const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!s) return;
  s.removeItem(SA_SESSION_KEY);
  s.removeItem(SA_USER_KEY);
}

/**
 * Derive the auth state from what we know. Pure.
 *   no cached session                       → 'none'
 *   cached + validation not yet done         → 'validating'
 *   cached + validation ok                   → 'valid'
 *   cached + validation 401/failed-auth      → 'expired'
 */
export function deriveSessionState({ hasCached, validation }) {
  if (!hasCached) return 'none';
  if (validation === 'ok') return 'valid';
  if (validation === 'expired') return 'expired';
  return 'validating';
}

/** One-shot guard: runs the handler at most once until reset(). Stops 401 storms and redirect loops. */
export function createOnceGuard() {
  let fired = false;
  return {
    run(fn) { if (fired) return false; fired = true; if (typeof fn === 'function') fn(); return true; },
    reset() { fired = false; },
    get fired() { return fired; },
  };
}
